import React, { useState, useEffect } from 'react';
import { Col, Form, Row, Button } from 'react-bootstrap';
import { CiTimer } from "react-icons/ci";
import './timings-history.css';
import TimingLayout from '../../Components/TimingLayout';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { FaClock, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';
import ReasonModal from './ReasonModal';

const rowsPerPage = 10;

export default function TimingsHistory() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [reasonType, setReasonType] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (startDate && endDate) {
        setLoading(true);
        try {
          const userId = localStorage.getItem("userId");
          const response = await axios.get(`https://backend.ems.hiradentallab.com/api/attendance/filter-attendance`, {
            params: {
              startDate: new Date(startDate).toISOString(),
              endDate: new Date(endDate).toISOString(),
              userId
            }
          });

          if (response.data.success) {
            setData(response.data.data);
          } else {
            setData([]);
            alert(response.data.message);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          alert(error.response?.data?.message || "Error while fetching data.");
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [startDate, endDate]);

  const validateForm = () => {
    const errors = {};
    if (!startDate) errors.startDate = 'Start Date is required';
    if (!endDate) errors.endDate = 'End Date is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGenerateReport = () => {
    if (validateForm()) {
      setShowTable(true);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const getRowClassName = (item) => {
    if ((item.lateMinutes && item.lateMinutes > 0) ||
        (item.earlyCloseMinutes && item.earlyCloseMinutes > 0)) {
      return 'late-row';
    }
    return 'on-time-row';
  };

  const exportToExcel = () => {
    const dataForExport = [
      ["SR", "Date", "CheckIn", "CheckOut", "Worked Hours", "Late Minutes", "Early Close Minutes", "Over-Time"],
      ...data.map((item, index) => [
        (currentPage - 1) * rowsPerPage + index + 1,
        formatDate(item.date),
        formatTime(item.checkIn),
        formatTime(item.checkOut),
        item.workedHours || '-',
        item.lateMinutes || '-',
        item.earlyCloseMinutes || '-',
        item.extraMinutes || '-'
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(dataForExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'TimingsHistory');
    XLSX.writeFile(wb, 'TimingsHistory.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Timings History', 14, 16);
    doc.autoTable({
      startY: 30,
      head: [['SR', 'Date', 'CheckIn', 'CheckOut', 'Worked Hours', 'Late Minutes', 'Early Close Minutes', 'Over-Time']],
      body: data.map((item, index) => [
        (currentPage - 1) * rowsPerPage + index + 1,
        formatDate(item.date),
        formatTime(item.checkIn),
        formatTime(item.checkOut),
        item.workedHours || '-',
        item.lateMinutes || '-',
        item.earlyCloseMinutes || '-',
        item.extraMinutes || '-'
      ]),
      theme: 'grid',
    });
    doc.save('TimingsHistory.pdf');
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(data.length / rowsPerPage)) {
      setCurrentPage(pageNumber);
    }
  };

  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(data.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);
    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleReasonClick = (record, type) => {
    setCurrentRecord(record);
    setReasonType(type);
    setShowReasonModal(true);
  };

  const handleSubmitReason = async (reason) => {
    try {
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName');
      const department = localStorage.getItem('department');

      if (!userId) {
        alert('Please log in to submit a reason');
        return;
      }

      const response = await axios.post(
        `https://backend.ems.hiradentallab.com/api/attendance/${currentRecord._id}/reason`,
        {
          type: reasonType,
          reason,
          userId,
          userName,
          department
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        const updatedData = data.map(item => {
          if (item._id === currentRecord._id) {
            return {
              ...item,
              [`${reasonType}Reason`]: reason,
              [`${reasonType}Approved`]: false
            };
          }
          return item;
        });

        setData(updatedData);
        setShowReasonModal(false);
        alert('Reason submitted successfully!');
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error submitting reason:', error);
      alert(error.response?.data?.message || error.message || 'Failed to submit reason');
    }
  };

  const renderReasonButton = (record, type) => {
    const reasonField = `${type}Reason`;
    const approvedField = `${type}Approved`;
    const minutesField = type === 'late' ? 'lateMinutes' :
                        type === 'earlyClose' ? 'earlyCloseMinutes' :
                        'extraMinutes';

    if (record[minutesField] && record[minutesField] > 0) {
      if (!record[reasonField] || record[reasonField] === '') {
        return (
          <Button
            variant="link"
            size="sm"
            onClick={() => handleReasonClick(record, type)}
            className="reason-button"
            title="Add Reason"
          >
            <FaClock className="reason-icon" />
          </Button>
        );
      } else if (record[reasonField] && !record[approvedField]) {
        return (
          <Button
            variant="link"
            size="sm"
            onClick={() => handleReasonClick(record, type)}
            className="reason-button"
            title="View Pending Reason"
          >
            <FaHourglassHalf className="reason-icon pending" />
          </Button>
        );
      } else if (record[reasonField] && record[approvedField]) {
        return (
          <Button
            variant="link"
            size="sm"
            onClick={() => handleReasonClick(record, type)}
            className="reason-button"
            title="View Approved Reason"
          >
            <FaCheckCircle className="reason-icon approved" />
          </Button>
        );
      }
    }
    return null;
  };

  return (
    <div className='container-fluid'>
      <TimingLayout>
        <div>
          <h1 className='timings-history-title'>
            <CiTimer className='timings-history-icon' /> Timing History
          </h1>
          <hr style={{ width: '100%' }} />
          <Form className='timings-history-form'>
            <Row>
              <Col md={6} xs={12}>
                <Form.Group>
                  <Form.Label>Start date <span className='required'>*</span> :</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={startDate || ''}
                    onChange={(e) => setStartDate(e.target.value)}
                    isInvalid={!!formErrors.startDate}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.startDate}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6} xs={12}>
                <Form.Group>
                  <Form.Label>End date <span className='required'>*</span> :</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={endDate || ''}
                    onChange={(e) => setEndDate(e.target.value)}
                    isInvalid={!!formErrors.endDate}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.endDate}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Button
              className='btn btn-primary generate-report-button'
              type='button'
              onClick={handleGenerateReport}>
              Generate Report
            </Button>
          </Form>
          {showTable && (
            <div className='timings-history-table-container'>
              <div>
                <Row>
                  <Col xs={12} md={6}>
                    <Button variant="secondary" onClick={exportToExcel} style={{marginRight:'5px'}}>Excel</Button>
                    <Button variant="secondary" onClick={exportToPDF}>PDF</Button>
                  </Col>
                </Row>
              </div>
              <div className="table-responsive">
                <table className='table table-striped'>
                  <thead>
                    <tr className="time-history-table-header">
                      <th>SR</th>
                      <th>Date</th>
                      <th>CheckIn</th>
                      <th>CheckOut</th>
                      <th>Worked Hours</th>
                      <th>Late Minutes</th>
                      <th>Early Close Minutes</th>
                      <th>Over-Time</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="9" style={{ textAlign: 'center' }}>
                          Loading...
                        </td>
                      </tr>
                    ) : data.length > 0 ? (
                      data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage).map((item, index) => (
                        <tr
                          key={item._id}
                          className={getRowClassName(item)}
                        >
                          <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                          <td>{formatDate(item.date)}</td>
                          <td>{formatTime(item.checkIn)}</td>
                          <td>{formatTime(item.checkOut)}</td>
                          <td>{item.workedHours || '-'}</td>
                          <td>
                            {item.lateMinutes || '-'}
                            {renderReasonButton(item, 'late')}
                          </td>
                          <td>
                            {item.earlyCloseMinutes || '-'}
                            {renderReasonButton(item, 'earlyClose')}
                          </td>
                          <td>
                            {item.extraMinutes || '-'}
                            {renderReasonButton(item, 'overtime')}
                          </td>
                          <td>
                            {/* Additional actions can be added here */}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" style={{ textAlign: 'center' }}>
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="timing-history-table-footer">
                    <tr className="table-header-footer">
                      <td>Total</td>
                      <td>{data.length}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="pagination-container">
                <Row className="custom-row">
                  <Col xs={12} md={9} className="custom-col-left">
                    Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, data.length)} of {data.length} entries
                  </Col>
                  <Col xs={12} md={3} className="custom-col-right">
                    <nav>
                      <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <a
                            href="/#"
                            className="page-link"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(currentPage - 1);
                            }}
                          >
                            Previous
                          </a>
                        </li>
                        {pageRange().map((pageNumber) => (
                          <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                            <a
                              href="/#"
                              className="page-link"
                              onClick={(e) => {
                                e.preventDefault();
                                handlePageChange(pageNumber);
                              }}
                            >
                              {pageNumber}
                            </a>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === Math.ceil(data.length / rowsPerPage) ? 'disabled' : ''}`}>
                          <a
                            href="/#"
                            className="page-link"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(currentPage + 1);
                            }}
                          >
                            Next
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </Col>
                </Row>
              </div>
            </div>
          )}
          <ReasonModal
            show={showReasonModal}
            onHide={() => setShowReasonModal(false)}
            onSubmit={handleSubmitReason}
            type={reasonType}
            record={currentRecord}
          />
        </div>
      </TimingLayout>
    </div>
  );
}